import { memo } from 'react';
import { Card } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { GROUP_PERMISSIONS } from '../../constants/groupPermissions';
import InlineEditField from './InlineEditField';

/**
 * Header card of the group detail: name and description with inline edit, the member count and an
 * `actions` slot (Delete group). The pencils are gated here (`GROUP_PERMISSIONS.MANAGE`); the caller gates `actions`.
 *
 * @param {object} props
 * @param {{ name: string, description: string, memberCount: number }} props.group
 * @param {(name: string) => Promise<void>} props.onRename
 * @param {(description: string) => Promise<void>} props.onRedescribe
 * @param {import('react').ReactNode} [props.actions]
 */
const GroupHeaderCard = ({ group, onRename, onRedescribe, actions }) => {
  const { t } = useTranslation('people-access');
  const { name, description, memberCount } = group;

  return (
    <Card className="flex items-start justify-between gap-6 p-6">
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <InlineEditField
          as="h2"
          value={name}
          label={t('groups.detail.nameLabel')}
          editLabel={t('groups.detail.editName')}
          onSave={onRename}
          permission={GROUP_PERMISSIONS.MANAGE}
          required
          requiredMessage={t('groups.detail.nameRequired')}
          valueClassName="text-lg font-semibold text-content"
        />
        <InlineEditField
          value={description}
          label={t('groups.detail.descriptionLabel')}
          editLabel={t('groups.detail.editDescription')}
          emptyText={t('groups.detail.noDescription')}
          onSave={onRedescribe}
          permission={GROUP_PERMISSIONS.MANAGE}
          multiline
          valueClassName="text-sm text-content-secondary"
        />
      </div>
      <div className="flex shrink-0 items-center gap-4">
        <div data-testid="group-member-count" className="flex flex-col items-center">
          <span className="text-2xl font-semibold text-content">{memberCount}</span>
          <span className="text-xs text-content-muted">
            {t('groups.detail.memberCount', { count: memberCount })}
          </span>
        </div>
        {actions}
      </div>
    </Card>
  );
};

export default memo(GroupHeaderCard);
